import type React from "react"
import { useCallback, useLayoutEffect, useRef, useState } from "react"
import { insertIntoSortedArray } from "@multimediallc/web-utils"
import {
    isScrolledIntoView,
    stopScrollMomentum,
} from "@multimediallc/web-utils/domUtils"
import { isiOS } from "@multimediallc/web-utils/modernizr"
import { ChatEntityType } from "../../../interfaces/chat"
import { selectAppPanelVersion } from "../../../store/appPanelSlice"
import { selectMobileBroadcastChat } from "../../../store/chatSlice"
import { useAppDispatch, useAppSelector } from "../../../store/hooks"
import {
    compareChatTimestamp,
    copyChatTimestamp,
} from "../../../utils/chatUtil"
import { mergeClasses } from "../../../utils/css"
import { addPageAction } from "../../../utils/newrelic"
import { ScrollToBottomButton } from "../../chat/ScrollToBottomButton"
import { ChatEntity } from "../ChatEntity"
import {
    APP_PANEL_MIN_ELAPSED_MESSAGES,
    MAX_BROADCASTER_CHAT_HISTORY,
    NEAR_BOTTOM_THRESHOLD,
} from "./constants"
import type {
    AppPanelData,
    ChatEntityData,
    ChatEntityID,
} from "../../../interfaces/chat"
import "./MobileBroadcastChat.scss"

export function MobileBroadcastChat() {
    const chatRef = useRef<HTMLDivElement | null>(null)
    const newMessageLineLocationRef = useRef<HTMLDivElement | null>(null)
    const appPanelRef = useRef<HTMLDivElement | null>(null)

    // May eventually want to move read state into redux, but for now is only for rendering logic in this one chat component
    const [latestReadID, setLatestReadID] = useState<ChatEntityID | undefined>(
        undefined,
    )
    const [newMessagesLineLocationID, setNewMessagesLineLocationID] = useState<
        ChatEntityID | undefined
    >(undefined)

    const [isScrolledToBottom, setIsScrolledToBottom] = useState<boolean>(true)

    const chatContents = useAppSelector(selectMobileBroadcastChat).slice(
        -1 * MAX_BROADCASTER_CHAT_HISTORY,
    )

    const latestReadIndex =
        chatContents.findLastIndex(({ id }) => id === latestReadID) ??
        chatContents.length - 1
    const numUnread = chatContents.length - latestReadIndex - 1

    const isChatScrolledToBottom = () => {
        const currentChat = chatRef.current
        if (currentChat === null) {
            return false
        }
        // Due to `flex-direction: column-reverse`, scrollTop has positive values for overscroll and negative values for normal scroll
        return (
            currentChat.scrollTop > 0 ||
            -1 * currentChat.scrollTop < NEAR_BOTTOM_THRESHOLD
        )
    }

    const updateLatestRead = useCallback(() => {
        if (chatContents.length === 0) {
            return
        }
        setLatestReadID(chatContents[chatContents.length - 1].id)
    }, [chatContents])

    const onScroll = () => {
        const scrolledToBottom = isChatScrolledToBottom()
        if (scrolledToBottom) {
            updateLatestRead()
        }
        setIsScrolledToBottom(scrolledToBottom)
    }

    const scrollToBottom = useCallback(() => {
        addPageAction("MobileBroadcastScrollToBottomClicked")
        if (chatRef.current === null) {
            return
        }
        if (isiOS()) {
            stopScrollMomentum(chatRef.current)
        }
        chatRef.current.scrollTop = 0
        setIsScrolledToBottom(true)
    }, [])

    const initialChatScrollTop = chatRef.current?.scrollTop
    const initialChatScrollHeight = chatRef.current?.scrollHeight
    useLayoutEffect(() => {
        if (isChatScrolledToBottom() || chatRef.current === null) {
            updateLatestRead()
        } else {
            // Unfortunately ios does not fully support css scroll anchoring so we still need to
            // fix scroll position in the case of new renders while scrolled up
            if (
                initialChatScrollTop !== undefined &&
                initialChatScrollHeight !== undefined
            ) {
                const newChatHeight = chatRef.current.scrollHeight
                // Subtract height difference because `flex-direction: column-reverse` means scrollTop is measured in negative units from the bottom
                chatRef.current.scrollTop =
                    initialChatScrollTop -
                    (newChatHeight - initialChatScrollHeight)
            }

            // Only move the new message line if the current new message line is not visible and we are scrolled up in chat
            const newMessageLineOutOfView =
                !newMessageLineLocationRef.current ||
                !isScrolledIntoView(
                    newMessageLineLocationRef.current,
                    chatRef.current,
                )
            if (newMessageLineOutOfView && numUnread > 0) {
                setNewMessagesLineLocationID(
                    chatContents[chatContents.length - numUnread].id,
                )
            }
        }
    }, [numUnread, updateLatestRead, chatContents])

    const [appPanelData, setAppPanelData] = useState<AppPanelData>({
        derivedTimestamp: 0,
        timeReceived: 0,
        entityType: ChatEntityType.AppPanel,
        id: "appPanel",
    })
    const chatContentsWithAppPanel = useInsertAppPanel(
        chatContents,
        appPanelData,
    )
    useCheckAppPanelUpdate(
        chatContentsWithAppPanel,
        chatRef,
        appPanelRef,
        appPanelData,
        setAppPanelData,
    )

    return (
        <div
            className={mergeClasses(
                "MobileBroadcastChat",
                isScrolledToBottom ? "MobileBroadcastChat--scrolledDown" : "",
            )}
            ref={chatRef}
            onScroll={onScroll}
            data-testid="mobile-broadcast-chat"
        >
            <div
                className="MobileBroadcastChat__Messages"
                data-testid="mobile-broadcast-chat-messages"
            >
                {chatContentsWithAppPanel.map((chatEntity) => {
                    const isOldestUnread =
                        chatEntity.id === newMessagesLineLocationID
                    const isAppPanel =
                        chatEntity.entityType === ChatEntityType.AppPanel
                    // Once on React 19, we should see if this can be simplified to pass the ref directly to ChatEntity
                    // without breaking ChatEntity's memo due to the ref callback being a new func on every render
                    return (
                        <div
                            key={chatEntity.id}
                            ref={(el) => {
                                if (el === null) {
                                    return
                                }
                                if (isOldestUnread) {
                                    newMessageLineLocationRef.current = el
                                }
                                if (isAppPanel) {
                                    appPanelRef.current = el
                                }
                            }}
                        >
                            <ChatEntity
                                entityData={chatEntity}
                                isOldestUnread={isOldestUnread}
                            />
                        </div>
                    )
                })}
            </div>
            <div className="MobileBroadcastChat__ScrollDownButtonContainer">
                {!isScrolledToBottom && (
                    <ScrollToBottomButton
                        onClick={scrollToBottom}
                        numUnread={numUnread}
                    />
                )}
            </div>
            <div className="MobileBroadcastChat__ScrollAnchor" />
        </div>
    )
}

function useInsertAppPanel(
    chatContents: ChatEntityData[],
    appPanelData: AppPanelData,
): ChatEntityData[] {
    if (appPanelData.timeReceived !== 0) {
        return insertIntoSortedArray(
            chatContents,
            appPanelData,
            compareChatTimestamp,
        )
    }
    return chatContents
}

// On app panel data update, update app panel's chat timestamp to move it to the bottom of chat if and only
// if the current app panel is not visible and enough messages have come in after its current location
function useCheckAppPanelUpdate(
    chatContents: ChatEntityData[],
    chatRef: React.MutableRefObject<HTMLDivElement | null>,
    appPanelRef: React.MutableRefObject<HTMLDivElement | null>,
    appPanelData: AppPanelData,
    setAppPanelData: (data: AppPanelData) => void,
): void {
    const appPanelVersion = useAppSelector(selectAppPanelVersion)
    const prevAppPanelVersion = useRef(appPanelVersion)
    const dispatch = useAppDispatch()
    useLayoutEffect(() => {
        if (
            appPanelVersion === prevAppPanelVersion.current ||
            !chatRef.current
        ) {
            return
        }
        prevAppPanelVersion.current = appPanelVersion

        if (!appPanelRef.current) {
            const newAppPanelData = copyChatTimestamp(
                chatContents[chatContents.length - 1],
                appPanelData,
            )
            setAppPanelData(newAppPanelData)
            return
        }

        const appPanelOutOfView = !isScrolledIntoView(
            appPanelRef.current,
            chatRef.current,
        )
        const appPanelIndex = chatContents.findIndex(
            (entity) => entity.entityType === ChatEntityType.AppPanel,
        )
        const messagesElapsed = chatContents.length - appPanelIndex - 1
        if (
            appPanelOutOfView &&
            messagesElapsed >= APP_PANEL_MIN_ELAPSED_MESSAGES
        ) {
            const newAppPanelData = copyChatTimestamp(
                chatContents[chatContents.length - 1],
                appPanelData,
            )
            setAppPanelData(newAppPanelData)
        }
    }, [
        chatContents,
        appPanelVersion,
        dispatch,
        chatRef,
        appPanelRef,
        appPanelData,
        setAppPanelData,
    ])
}
